/**
 * Build the contract FeedbackContext envelope for CheckList.
 *
 * Privacy invariant (CUSTOMER_FEEDBACK_PLAN §4.3 / A-5 — verify by reading this
 * builder, not field names):
 * - Structural facts only: counts, kind, model id, cache-hit, HTTP status,
 *   error name/message/truncated stack, elapsed ms.
 * - Never: list item text, list/template/checklist names, notes, screen names,
 *   other members' emails, avatar data, share/invite tokens, JWTs, or arbitrary
 *   localStorage contents.
 * - Share-link routes contain a capability token in the path; redact them.
 */

import type { FeedbackContext } from "./client.js";
import { APP_VERSION } from "./client.js";
import type { SurfaceId } from "./surfaces.js";

const SESSION_KEY = "feedbackSessionRef:v1";

function getSessionRef(): string {
  try {
    let ref = sessionStorage.getItem(SESSION_KEY);
    if (!ref) {
      ref =
        typeof crypto !== "undefined" && "randomUUID" in crypto
          ? crypto.randomUUID()
          : `s-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
      sessionStorage.setItem(SESSION_KEY, ref);
    }
    return ref;
  } catch {
    return `s-${Date.now().toString(36)}`;
  }
}

function deviceClass(): FeedbackContext["deviceClass"] {
  const w = window.innerWidth;
  if (w < 768) return "mobile";
  if (w < 1024) return "tablet";
  return "desktop";
}

function parseBrowser(ua: string): string {
  const edge = ua.match(/Edg\/(\d+)/);
  if (edge) return `Edge ${edge[1]}`;
  const opera = ua.match(/OPR\/(\d+)/);
  if (opera) return `Opera ${opera[1]}`;
  const chrome = ua.match(/Chrome\/(\d+)/);
  if (chrome && !/Chromium/.test(ua)) return `Chrome ${chrome[1]}`;
  const firefox = ua.match(/Firefox\/(\d+)/);
  if (firefox) return `Firefox ${firefox[1]}`;
  const safari = ua.match(/Version\/(\d+).+Safari/);
  if (safari) return `Safari ${safari[1]}`;
  return "Unknown";
}

function parseOs(ua: string): string {
  if (/Windows NT 10/.test(ua))
    return /Windows NT 10\.0; Win64/.test(ua) ? "Windows 10/11" : "Windows 10";
  if (/Windows NT 6\.3/.test(ua)) return "Windows 8.1";
  if (/Mac OS X (\d+)[._](\d+)/.test(ua)) {
    const m = ua.match(/Mac OS X (\d+)[._](\d+)/);
    return m ? `macOS ${m[1]}.${m[2]}` : "macOS";
  }
  if (/Android (\d+(?:\.\d+)?)/.test(ua)) {
    const m = ua.match(/Android (\d+(?:\.\d+)?)/);
    return m ? `Android ${m[1]}` : "Android";
  }
  if (/iPhone OS (\d+)[._](\d+)/.test(ua) || /iPad; CPU OS (\d+)[._](\d+)/.test(ua)) {
    const m = ua.match(/(?:iPhone OS|CPU OS) (\d+)[._](\d+)/);
    return m ? `iOS ${m[1]}.${m[2]}` : "iOS";
  }
  if (/Linux/.test(ua)) return "Linux";
  return "Unknown";
}

/**
 * Redact capability tokens from pathnames before they leave the browser (§4.3).
 * `/s/<token>` must become `/s/[token]` — a leaked share token is a live grant.
 * Also redact invite, share-decision, and request-access token segments for the
 * same reason (share-decision carries the owner's approve/deny capability token,
 * AccessDecisionPage.tsx).
 */
export function redactRoute(pathname: string): string {
  let route = pathname;
  route = route.replace(/^(\/s\/)[^/]+/i, "$1[token]");
  route = route.replace(/^(\/invite\/)[^/]+/i, "$1[token]");
  route = route.replace(/^(\/share-decision\/)[^/]+/i, "$1[token]");
  route = route.replace(
    /^(\/request-access\/[^/]+\/)[^/]+/i,
    "$1[id]",
  );
  return route;
}

function resolveAppId(): string {
  const fromEnv = import.meta.env.VITE_FEEDBACK_APP_ID;
  if (typeof fromEnv === "string" && fromEnv.trim()) return fromEnv.trim();
  return "checklist";
}

function resolveAppVersion(): string {
  return APP_VERSION;
}

export interface BuildContextOptions {
  surfaceId: SurfaceId;
  /** Allowlisted non-sensitive facts only — never list names or item text. */
  payload?: Record<string, unknown>;
  /** Override route (pathname only). Defaults to location.pathname (then redacted). */
  route?: string;
}

/**
 * Assemble the context envelope. Callers must not put user content in `payload`.
 */
export function buildFeedbackContext(opts: BuildContextOptions): FeedbackContext {
  const ua = typeof navigator !== "undefined" ? navigator.userAgent : "";
  const rawRoute =
    opts.route ?? (typeof window !== "undefined" ? window.location.pathname : "/");
  const route = redactRoute(rawRoute);

  let timezone = "UTC";
  try {
    timezone = Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";
  } catch {
    // keep UTC
  }

  const prefersDark =
    typeof window !== "undefined" &&
    window.matchMedia &&
    window.matchMedia("(prefers-color-scheme: dark)").matches;

  return {
    version: 1,
    appId: resolveAppId(),
    appVersion: resolveAppVersion(),
    surfaceId: opts.surfaceId,
    route,
    occurredAt: new Date().toISOString(),
    timezone,
    locale: typeof navigator !== "undefined" ? navigator.language || "en-US" : "en-US",
    viewport: typeof window !== "undefined" ? `${window.innerWidth}x${window.innerHeight}` : "0x0",
    deviceClass: typeof window !== "undefined" ? deviceClass() : "desktop",
    browser: parseBrowser(ua),
    os: parseOs(ua),
    prefersColorScheme: prefersDark ? "dark" : "light",
    sessionRef: getSessionRef(),
    ...(opts.payload && Object.keys(opts.payload).length > 0 ? { payload: opts.payload } : {}),
  };
}

/** Context field labels shown in the "What's included with this report" disclosure. */
export const CONTEXT_DISCLOSURE_FIELDS: { key: string; label: string }[] = [
  { key: "route", label: "Current page path" },
  { key: "browser", label: "Browser (family + major version)" },
  { key: "os", label: "Operating system" },
  { key: "deviceClass", label: "Device class (mobile / tablet / desktop)" },
  { key: "viewport", label: "Viewport size" },
  { key: "timezone", label: "Timezone" },
  { key: "locale", label: "Locale" },
  { key: "prefersColorScheme", label: "Color scheme preference" },
  { key: "appVersion", label: "App version" },
  { key: "surfaceId", label: "Which form / tool surface" },
];
